import { Query, useQueries, UseQueryResult } from '@tanstack/react-query';

import { sharedQueryKeys, useNegotiateWebsocketUrl } from '@services/queries';
import { NegotiateWebsocketUrlHubs, RawSpeechTokenResponse } from '@services/types';
import { calculateRefetchInterval } from '@utils/time';

import { getAvatarSupportedLocales, getAvatarTokenAndRegion } from './api';
import { AvatarSupportedLocalesResponse } from './types';

const queryKeys = {
    avatarToken: 'avatarToken',
} as const;

export const useGetAvatarConnectionData = () => {
    const negotiateWebsocketUrlQuery = useNegotiateWebsocketUrl(
        NegotiateWebsocketUrlHubs.AvatarVoiceStream
    );

    return useQueries({
        queries: [
            {
                queryKey: [queryKeys.avatarToken],
                queryFn: getAvatarTokenAndRegion,
                refetchInterval: (query: Query<RawSpeechTokenResponse, Error>) =>
                    calculateRefetchInterval(query.state.data),
                refetchIntervalInBackground: true,
            },
            {
                queryKey: [sharedQueryKeys.avatarSupportedLocales],
                queryFn: getAvatarSupportedLocales,
            },
        ],
        combine: (
            results: [
                UseQueryResult<RawSpeechTokenResponse, Error>,
                UseQueryResult<AvatarSupportedLocalesResponse, Error>,
            ]
        ) => {
            const [avatarTokenResult, avatarSupportedLocalesResult] = results;

            return {
                data: {
                    avatarToken: avatarTokenResult.data,
                    websocketUrl: negotiateWebsocketUrlQuery.data,
                    avatarSupportedLocales: avatarSupportedLocalesResult.data,
                },
                isLoading: results.some((r) => r.isLoading) || negotiateWebsocketUrlQuery.isLoading,
                error:
                    results.find((r) => r.error)?.error ?? negotiateWebsocketUrlQuery.error ?? null,
                isWebsocketUrlFetching: negotiateWebsocketUrlQuery.isFetching,
            };
        },
    });
};
